import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import CardOne from '@/components/common/card/CardOne'
import NormalTopBar from '@/components/common/bar/NormalTopBar'
import CommonModal from '@/components/common/modal/CommonModal'
import { updateNotificationSettingsApi, getNotificationSettingsApi } from '@/api/notificationApi'
import { getUserId } from '@/utils/auth'
import { useAuthStore } from '@/store/authStore'
import { getFcmToken } from '@/utils/fcmToken'
import { LOCAL_STORAGE_KEYS, safeLocalStorage } from '@/constants/storageKeys'

interface ToggleSwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  ariaLabel: string
  disabled?: boolean // 추가: mutation 진행 중 비활성화
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange, ariaLabel, disabled = false }) => {
  return (
    <button
      type='button'
      role='switch'
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`w-10 h-5 rounded-full relative transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 ${
        checked ? 'bg-amber-500' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transform transition-transform duration-200 ease-in-out ${
          checked ? 'translate-x-5' : ''
        }`}
      ></span>
    </button>
  )
}

const PushSettingPage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const accessToken = useAuthStore.getState().getAccessToken()
  const userId = getUserId(accessToken || '')
  const fcmToken = getFcmToken()

  const [consultationNotifications, setConsultationNotifications] = useState(true)
  const [announcementNotifications, setAnnouncementNotifications] = useState(true)
  const [missionNotifications, setMissionNotifications] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSettingId, setSelectedSettingId] = useState<string | null>(null)

  // Firestore에서 알림 설정 가져오기 - 설정 최적화
  const {
    data: notificationSettings,
    isLoading,
    error
  } = useQuery({
    queryKey: ['notificationSettings', userId, fcmToken],
    queryFn: async () => {
      if (!userId || !fcmToken) {
        console.log('No userId or fcmToken found for notification settings', { userId: !!userId, fcmToken: !!fcmToken })
        return null
      }
      console.log('Fetching notification settings for userId:', userId, 'fcmToken:', fcmToken.substring(0, 20) + '...')
      try {
        const response = await getNotificationSettingsApi(userId, fcmToken)
        console.log('Notification settings response:', response)
        return response
      } catch (error) {
        console.error('Failed to fetch notification settings:', error)
        throw error
      }
    },
    enabled: !!userId && !!fcmToken,
    retry: 1,
    staleTime: 0, // 변경: 항상 fresh data 요구
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true, // 변경: 창 포커스 시 재조회
    refetchOnMount: true // 변경: 컴포넌트 마운트 시 항상 재조회
  })

  // userId, fcmToken 로그
  useEffect(() => {
    console.log('PushSettingPage - userId:', userId, 'fcmToken:', !!fcmToken, 'accessToken:', !!accessToken)
  }, [userId, fcmToken, accessToken])

  // Firestore 설정으로 상태 초기화
  useEffect(() => {
    if (notificationSettings?.data?.settings) {
      const settings = notificationSettings.data.settings
      console.log('Updating state with settings:', settings)
      setConsultationNotifications(settings.consultation)
      setAnnouncementNotifications(settings.common)
      setMissionNotifications(settings.marketing)
    }
  }, [notificationSettings])

  // 알림 설정 업데이트 mutation - Optimistic Update 적용
  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: { common: boolean; marketing: boolean; consultation: boolean }) => {
      if (!userId) throw new Error('사용자 ID를 찾을 수 없습니다')
      if (!fcmToken) throw new Error('FCM 토큰을 찾을 수 없습니다')

      console.log('Updating notification settings:', { userId, fcmToken: fcmToken.substring(0, 20) + '...', settings })

      const response = await updateNotificationSettingsApi({
        userId,
        fcmToken,
        settings
      })
      console.log('Update response:', response)
      return response
    },

    // Optimistic update: API 호출 전 즉시 UI 업데이트
    onMutate: async newSettings => {
      // 진행 중인 refetch 취소
      await queryClient.cancelQueries({ queryKey: ['notificationSettings', userId, fcmToken] })

      // 이전 값 스냅샷 저장
      const previousSettings = queryClient.getQueryData(['notificationSettings', userId, fcmToken])

      // Optimistic update 적용
      queryClient.setQueryData(['notificationSettings', userId, fcmToken], (old: any) => ({
        ...old,
        data: {
          ...old?.data,
          settings: newSettings
        }
      }))

      // 로컬 스토리지에도 즉시 저장
      safeLocalStorage.setJSON(LOCAL_STORAGE_KEYS.NOTIFICATION_SETTINGS, newSettings)

      // rollback을 위해 이전 설정 반환
      return { previousSettings }
    },

    // 에러 발생 시 rollback
    onError: (error: any, _, context) => {
      console.error('알림 설정 업데이트 실패:', error)
      console.error('Error details:', error.response?.data)

      // 이전 설정으로 롤백
      if (context?.previousSettings) {
        queryClient.setQueryData(['notificationSettings', userId, fcmToken], context.previousSettings)

        // 로컬 상태도 롤백
        const settings = (context.previousSettings as any)?.data?.settings
        if (settings) {
          setConsultationNotifications(settings.consultation)
          setAnnouncementNotifications(settings.common)
          setMissionNotifications(settings.marketing)
          safeLocalStorage.setJSON(LOCAL_STORAGE_KEYS.NOTIFICATION_SETTINGS, settings)
        }
      }

      // alert('알림 설정 업데이트에 실패했습니다. 다시 시도해주세요.')
    },

    // 성공 여부와 관계없이 실행
    onSettled: () => {
      // 서버 데이터로 동기화
      queryClient.invalidateQueries({ queryKey: ['notificationSettings', userId, fcmToken] })
    },

    onSuccess: data => {
      console.log('알림 설정이 성공적으로 업데이트되었습니다.', data)
    }
  })

  const handleToggleChange = (id: string, checked: boolean) => {
    // mutation 진행 중이면 무시
    if (updateSettingsMutation.isPending) return

    if (checked) {
      // Toggling on
      const newSettings = {
        common: announcementNotifications,
        marketing: missionNotifications,
        consultation: consultationNotifications
      }

      switch (id) {
        case 'consultation':
          setConsultationNotifications(true)
          newSettings.consultation = true
          break
        case 'announcement':
          setAnnouncementNotifications(true)
          newSettings.common = true
          break
        case 'mission':
          setMissionNotifications(true)
          newSettings.marketing = true
          break
      }

      // Firebase에 업데이트
      updateSettingsMutation.mutate(newSettings)
    } else {
      // Toggling off, open modal
      setSelectedSettingId(id)
      setIsModalOpen(true)
    }
  }

  const handleConfirmWithdrawal = () => {
    if (selectedSettingId) {
      const newSettings = {
        common: announcementNotifications,
        marketing: missionNotifications,
        consultation: consultationNotifications
      }

      switch (selectedSettingId) {
        case 'consultation':
          setConsultationNotifications(false)
          newSettings.consultation = false
          break
        case 'announcement':
          setAnnouncementNotifications(false)
          newSettings.common = false
          break
        case 'mission':
          setMissionNotifications(false)
          newSettings.marketing = false
          break
      }

      // Firebase에 업데이트
      updateSettingsMutation.mutate(newSettings)
    }
    setIsModalOpen(false)
    setSelectedSettingId(null)
  }

  const handleCancelWithdrawal = () => {
    setIsModalOpen(false)
    setSelectedSettingId(null)
  }

  const settings = [
    {
      id: 'consultation',
      title: '상담 알림',
      description: '상담할 시기가 되면 알려드려요.',
      checked: consultationNotifications,
      onChange: (checked: boolean) => handleToggleChange('consultation', checked)
    },
    {
      id: 'announcement',
      title: '공지사항 알림',
      description: '앱 업데이트 정보 등을 알려드려요.',
      checked: announcementNotifications,
      onChange: (checked: boolean) => handleToggleChange('announcement', checked)
    },
    {
      id: 'mission',
      title: '미션 알림',
      description: '미이행 미션이 있으면 알려드려요.',
      checked: missionNotifications,
      onChange: (checked: boolean) => handleToggleChange('mission', checked)
    }
  ]

  return (
    <div className='min-h-screen'>
      <NormalTopBar
        title='알림 설정'
        prevButton
        onPrevButtonClick={() => navigate('/my')}
        textClassName='text-xl text-text-header font-semibold'
        boxClassName='bg-gradient-to-r from-amber-400 to-yellow-300'
      />
      <div className='p-3 flex flex-col gap-3'>
        {isLoading ? (
          <div className='flex justify-center items-center py-10'>
            <div className='text-gray-500'>알림 설정을 불러오는 중...</div>
          </div>
        ) : error || !fcmToken ? (
          <div className='flex flex-col items-center py-10'>
            <div className='text-red-500 mb-2'>
              {!fcmToken ? 'FCM 토큰을 찾을 수 없습니다.' : '알림 설정을 불러오는데 실패했습니다.'}
            </div>
            <div className='text-gray-500 text-sm'>
              {!fcmToken ? '앱을 다시 실행해주세요.' : '기본 설정이 표시됩니다.'}
            </div>
          </div>
        ) : (
          settings.map(setting => (
            <CardOne key={setting.id} className='px-3 py-5 bg-white rounded-2xl'>
              <div className='flex justify-between items-center'>
                <p className='font-medium text-sm text-gray-500'>{setting.title}</p>
                <ToggleSwitch
                  checked={setting.checked}
                  onChange={() => setting.onChange(!setting.checked)}
                  ariaLabel={`${setting.title} 알림 ${setting.checked ? '켜짐' : '꺼짐'}`}
                  disabled={updateSettingsMutation.isPending} // mutation 진행 중 비활성화
                />
              </div>
              <hr className='border-amber-200 mt-2 mb-5' />
              <p className='text-gray-900'>{setting.description}</p>
              {/* mutation 진행 중 로딩 인디케이터 */}
              {updateSettingsMutation.isPending && <div className='mt-2 text-xs text-amber-600'>설정 저장 중...</div>}
            </CardOne>
          ))
        )}
      </div>
      {isModalOpen && (
        <CommonModal
          title='동의를 철회하시겠어요?'
          onClose={handleCancelWithdrawal}
          topButtonText='확인'
          bottomButtonText='취소'
          topButtonOnClick={handleConfirmWithdrawal}
          bottomButtonOnClick={handleCancelWithdrawal}
          hasCloseButton={false}
        />
      )}
    </div>
  )
}

export default PushSettingPage
